import { useState } from 'react';
import { categories, mainSiteUrl } from '@/data/categories';

const FORM_URL = 'https://readdy.ai/api/form/d9adnmsnib0mhnm9k3lg';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const hpValue = (formData.get('website_alt') as string || '').trim();
    if (hpValue) {
      setStatus('success');
      setEmail('');
      return;
    }

    setStatus('loading');
    setFormError('');

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: formData.get('email') as string }).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && (parsed as { code?: string }).code === 'OK') {
        setStatus('success');
        setEmail('');
      } else {
        const serverMsg = (parsed as { meta?: { message?: string } }).meta?.message
          || (parsed as { message?: string }).message
          || responseText;
        if (serverMsg && serverMsg.includes('spam')) {
          setFormError('Submission could not be processed. Please try again.');
        } else {
          setFormError(serverMsg || 'Something went wrong. Please try again.');
        }
        setStatus('error');
      }
    } catch {
      setFormError('Network error. Please check your connection and try again.');
      setStatus('error');
    }
  }

  const popularCategories = ['Shoes', 'T-Shirts', 'Hoodies & Sweaters'];

  return (
    <footer className="relative w-full bg-secondary-900 text-white">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-16">
          <div>
            <a href="/" className="inline-block mb-4">
              <span className="text-xl font-heading font-bold text-white">Superbuy</span>
              <span className="text-xl font-heading font-light text-white/60 ml-1">Spreadsheet</span>
            </a>
            <p className="text-sm text-white/50 leading-relaxed max-w-xs">
              The ultimate tool for organizing cross-border shopping. Track, compare, and purchase
              products from China with ease.
            </p>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-white/40 mb-5">
              Newsletter
            </h4>
            <form
              onSubmit={handleSubmit}
              data-readdy-form=""
              className="relative"
            >
              <div className="flex items-center border-b border-white/20 pb-2">
                <input
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  className="flex-1 bg-transparent text-sm text-white placeholder-white/30 outline-none py-1"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="text-white/50 hover:text-white transition-colors duration-200 ml-2 cursor-pointer disabled:opacity-50"
                >
                  <i className="ri-arrow-right-line text-lg" />
                </button>
              </div>
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="newsletter-hp"
              />
              {status === 'success' && (
                <p className="text-xs text-secondary-300 mt-2">Thanks for subscribing! Check your inbox soon.</p>
              )}
              {status === 'error' && formError && (
                <p className="text-xs text-red-400 mt-2">{formError}</p>
              )}
              <p className="text-[11px] text-white/25 mt-2 leading-relaxed">
                Weekly tips, deals, and spreadsheet updates. Unsubscribe anytime.
              </p>
            </form>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-white/40 mb-5">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {popularCategories.map((name) => {
                const cat = categories.find((c) => c.name === name);
                return (
                  <li key={name}>
                    <a
                      href={cat?.url || '#'}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-white/60 hover:text-white transition-colors duration-200"
                    >
                      {name}
                    </a>
                  </li>
                );
              })}
              <li>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-white/60 hover:text-white transition-colors duration-200"
                >
                  All Products
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-white/40 mb-5">
              Connect
            </h4>
            <ul className="space-y-3">
              <li>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-white/60 hover:text-white transition-colors duration-200 inline-flex items-center gap-2"
                >
                  <i className="ri-global-line" />
                  Main Website
                </a>
              </li>
              <li>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-white/60 hover:text-white transition-colors duration-200 inline-flex items-center gap-2"
                >
                  <i className="ri-shopping-bag-3-line" />
                  Shop Now
                </a>
              </li>
              <li>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-white/60 hover:text-white transition-colors duration-200 inline-flex items-center gap-2"
                >
                  <i className="ri-information-line" />
                  About Us
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8">
          <p className="text-5xl md:text-8xl lg:text-9xl font-heading font-black text-white/8 leading-none select-none tracking-tight text-center">
            SUPERBUY
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-between mt-6 gap-4">
            <p className="text-xs text-white/30">
              &copy; {new Date().getFullYear()} Superbuy Spreadsheet. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <a href="/" className="text-xs text-white/30 hover:text-white/50 transition-colors duration-200">
                Privacy
              </a>
              <a href="/" className="text-xs text-white/30 hover:text-white/50 transition-colors duration-200">
                Terms
              </a>
              <a href="/" className="text-xs text-white/30 hover:text-white/50 transition-colors duration-200">
                Disclaimer
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}